import { NetworkStatus } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import {
  ModBanner,
  ModButton,
  ModIconButton,
  ModMenu,
  ModMenuItem,
  ModSkeleton,
  ModTag,
  ModTooltip
} from '@moderneinc/design-system-components';
import {
  iconSize,
  semanticColors,
  shadows,
  typography
} from '@moderneinc/design-system-tokens';
import { Stack, styled } from '@mui/material';
import { listItemIconClasses } from '@mui/material/ListItemIcon';
import {
  RichTreeViewPro,
  TreeItem,
  type TreeItemProps,
  treeItemClasses,
  useRichTreeViewProApiRef,
  useTreeItemModel
} from '@mui/x-tree-view-pro';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { bindMenu, usePopupState } from 'material-ui-popup-state/hooks';
import { useSession } from 'next-auth/react';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useDebounce } from 'use-debounce';
import {
  MyOwnOrganizationsBucketDocument,
  type MyOwnOrganizationsBucketQuery,
  type MyOwnOrganizationsBucketQueryVariables,
  type OrganizationSelectorFragment,
  OrgChildrenDocument,
  type OrgChildrenQuery,
  type OrgChildrenQueryVariables,
  OrgSpineBootstrapDocument,
  type OrgSpineBootstrapQuery,
  type OrgSpineBootstrapQueryVariables,
  useOrganizationSelectorGridQuery
} from '../../__generated__/apollo-hooks';
import {
  DOHERTY_THRESHOLD,
  ORG_SEARCH_INPUT_ID
} from '../../constants/general';
import { dataOrThrow } from '../../helpers/graphql-error.helper';
import { logError } from '../../helpers/logger.helper';
import {
  GLOBAL_ORGANIZATION_ID,
  getOrganizationDisplayName,
  isOwnUserOrgBucket,
  isUserOrganization,
  isUserOrgGroupingNode,
  MY_ORGANIZATIONS_GROUPING,
  USERS_GROUPING
} from '../../helpers/organizations.helper';
import { toBoxShadow } from '../../helpers/shadow.helper';
import {
  userOrgChangedEvent,
  userOrgCreatedEvent
} from '../../hooks/use-app-event.hooks';
import { useCopyToClipboard } from '../../hooks/use-copy-to-clipboard.hook';
import {
  CheckmarkIcon,
  CopyIcon,
  GlobalOrganizationIcon,
  OrganizationIcon
} from '../../icons/icons';
import { useSelectedOrganizationStore } from '../../stores/organization-store';
import { CopyToClipboardWithTooltip } from '../utilities/copy-pasta/copy-to-clipboard-with-tooltip.component';
import { EmptyState } from '../utilities/empty-state/empty-state.component';
import { LocaleNumber } from '../utilities/locale-number/locale-number.component';
import { MiddleTruncatedLabel } from '../utilities/middle-truncated-label/middle-truncated-label.component';
import {
  OrgFilteredTree,
  TREE_ROW_FOCUS_RING_SX
} from './org-filtered-tree.component';
import { OrganizationsFooter } from './organizations.footer.component';
import type { OrgRowActionTarget } from './organizations.rows';
import {
  buildLazySpineTree,
  buildSpineFromHits,
  collectSearchHits,
  exactNameCasings,
  findBestSearchHitIndex,
  getDefaultExpandedItems,
  getLastVisibleItemId,
  isPlaceholderItemId,
  isSelectableOrgId,
  isShowMoreItemId,
  type OrganizationTreeItem,
  type SpineHitInput,
  showMoreItemFolderId
} from './organizations.rows';
import { OrganizationsTableErrorBoundary } from './organizations.table.error-boundary';

/**
 * Server-side page size for the on-demand `Organization.children` fetch.
 * Each "Show more" click fires another network request for the next page.
 */
const CHILDREN_FETCH_PAGE_SIZE = 100;

/** Bounds per-node resolution and payload only; the backend walks the whole tree per request. */
export const SEARCH_PAGE_SIZE = 50;
/** One character matches most of a large tenant; the page would be noise. */
const MIN_SEARCH_TERM_LENGTH = 2;

type OrgChildNode = NonNullable<
  NonNullable<
    NonNullable<OrgChildrenQuery['organization']['children']['edges']>[number]
  >['node']
>;

type OrgFragmentForSpine = OrganizationSelectorFragment & {
  parents?: ReadonlyArray<{
    id: string;
    name: string;
    children?: { count?: number | null } | null;
  }> | null;
  repositories?: { count?: number | null } | null;
  children?: { count?: number | null } | null;
  user?: { email?: string | null } | null;
};

const isUserOrgBucket = (org: {
  id?: string | null;
  name?: string | null;
}): boolean => isUserOrgGroupingNode(org.id, org.name);

const orgToSpineHit = (
  org: OrgFragmentForSpine,
  currentUserEmail?: string
): SpineHitInput => {
  const isUserOrg = isUserOrganization(org);
  const userEmail = org.user?.email ?? undefined;
  const isGlobal = org.id === GLOBAL_ORGANIZATION_ID;
  return {
    id: org.id,
    label: getOrganizationDisplayName(org),
    icon: isGlobal ? GlobalOrganizationIcon : undefined,
    isUserOrganization: isUserOrg,
    isEditable:
      isUserOrg &&
      !!currentUserEmail &&
      userEmail === currentUserEmail &&
      !isUserOrgBucket(org),
    repositoryCount: org.repositories?.count ?? undefined,
    childCount: org.children?.count ?? undefined,
    parents: (org.parents ?? []).map((p) => ({
      id: p.id,
      // Use the same display-name helper as the unsearched tree so the
      // global org shows as "Global" instead of its raw "ε" name.
      label: getOrganizationDisplayName(p),
      childCount: p.children?.count ?? undefined
    }))
  };
};

/**
 * Per-folder reveal state. The `hasMore` discriminator ensures `cursor` is
 * required iff the server has more pages, so `{ hasMore: true, cursor:
 * undefined }` is unrepresentable.
 */
type RevealedFolderState = {
  children: OrganizationTreeItem[];
} & ({ hasMore: false } | { hasMore: true; cursor: string });

// A grouping node (the `Users` branch, a `Users/<email>` owner bucket, or a
// legacy bucket) is structural — expandable but never selectable as an org.
const isNonSelectableId = (id: string): boolean => isUserOrgGroupingNode(id);

const ACTIONS_ATTR = 'data-org-actions';

type OrgActionsContextValue = {
  copyOrganizationName: (name: string) => void;
  openRowContextMenu: (
    event: React.MouseEvent,
    target: OrgRowActionTarget
  ) => void;
};

const OrgActionsContext = React.createContext<OrgActionsContextValue | null>(
  null
);

type LazyTreeContextValue = {
  loadingFolders: Set<string>;
  failedFolders: Set<string>;
};

const LazyTreeContext = React.createContext<LazyTreeContextValue>({
  loadingFolders: new Set(),
  failedFolders: new Set()
});

const ICON_BUTTON_SMALL_SIZE = 32;
const TREE_ITEM_INDENT = ICON_BUTTON_SMALL_SIZE + 4;
const TREE_ITEM_BASE_PL = 8;
const TREE_ROW_MIN_HEIGHT = 36;
const SKELETON_WIDTHS = [50, 87, 64, 61, 58, 55, 72, 69];

/**
 * `TreeItem` passes `ownerState` to any slot that is not a host component
 * (`appendOwnerState`), so a slot spreading its props onto a `div` leaks it into
 * the DOM. A string-tag `styled` filters props through emotion's `isPropValid`,
 * which drops `ownerState` and keeps `className` and `data-*`.
 */
const EmptyIconPlaceholder = styled('div')({
  width: ICON_BUTTON_SMALL_SIZE,
  height: ICON_BUTTON_SMALL_SIZE
});

/** Empty by design: it filters `ownerState` as above; the button sets the size. */
const ExpandCollapseContainer = styled('div')({});

/** Centered status message shown in place of the tree — error, empty, or retry. */
const InlineTreeMessage: FunctionComponent<{
  testId: string;
  message: string;
  onRetry?: () => void;
}> = ({ testId, message, onRetry }) => (
  <Stack
    data-testid={testId}
    sx={{
      alignItems: 'center',
      gap: 1,
      py: 4,
      px: 2
    }}
  >
    <span
      style={{
        fontSize: typography.fontSize.sm,
        color: semanticColors.color.text.secondary,
        textAlign: 'center'
      }}
    >
      {message}
    </span>
    {onRetry && (
      <ModButton variant="secondary" size="small" onClick={onRetry}>
        Try again
      </ModButton>
    )}
  </Stack>
);

const hoverSx = {
  '&:hover': {
    backgroundColor: semanticColors.button.secondary.bgHover
  }
};

const hoverActionsSx = {
  gap: 0.25,
  display: 'flex',
  visibility: 'hidden',
  flexShrink: 0,
  alignItems: 'center',
  ml: 'auto',
  [`.${treeItemClasses.content}:hover &, .${treeItemClasses.content}:focus-within &`]:
    {
      visibility: 'visible'
    }
} as const;

const CopyNameButton: FunctionComponent<{
  label: string;
}> = ({ label }) => (
  <CopyToClipboardWithTooltip text={label} tooltip="Copy to clipboard">
    {(copied) => (
      <span style={{ display: 'flex' }}>
        <ModIconButton
          size="small"
          aria-label={`Copy ${label} to clipboard`}
          sx={hoverSx}
        >
          {copied ? (
            <CheckmarkIcon
              size={iconSize.sm}
              color={semanticColors.color.status.success.solid}
            />
          ) : (
            <CopyIcon size={iconSize.sm} />
          )}
        </ModIconButton>
      </span>
    )}
  </CopyToClipboardWithTooltip>
);

const OrgRowActions: FunctionComponent<{
  model: OrganizationTreeItem;
  showButton: boolean;
}> = ({ model, showButton }) => (
  <Stack direction="row" {...{ [ACTIONS_ATTR]: true }} sx={hoverActionsSx}>
    {showButton ? (
      <CopyNameButton label={model.label} />
    ) : (
      <div
        style={{
          width: ICON_BUTTON_SMALL_SIZE,
          height: ICON_BUTTON_SMALL_SIZE
        }}
      />
    )}
  </Stack>
);

const CustomTreeItem = React.forwardRef<HTMLLIElement, TreeItemProps>(
  (props, ref) => {
    const model = useTreeItemModel(props.itemId);
    const typedModel = model as OrganizationTreeItem;
    const lazyState = React.useContext(LazyTreeContext);
    const actions = React.useContext(OrgActionsContext);

    // Placeholder rows exist solely to convince MUI that the parent is
    // expandable (see `PLACEHOLDER_ITEM_SUFFIX`). They render zero-height
    // so the user never sees them; the parent's `onExpandedItemsChange`
    // fetch swaps them out for real children.
    if (isPlaceholderItemId(props.itemId)) {
      return <li ref={ref} style={{ display: 'none' }} />;
    }

    const isShowMore = isShowMoreItemId(props.itemId);

    if (isShowMore) {
      const folderId = showMoreItemFolderId(props.itemId);
      const isLoading = lazyState.loadingFolders.has(folderId);
      const hasFailed = lazyState.failedFolders.has(folderId);
      const label = hasFailed
        ? 'Failed to load — click to retry'
        : isLoading
          ? 'Loading…'
          : '+ Show more';
      return (
        <TreeItem
          {...props}
          ref={ref}
          slots={{ ...props.slots, iconContainer: EmptyIconPlaceholder }}
          label={
            <span
              style={{
                fontSize: typography.fontSize.xs,
                color: hasFailed
                  ? semanticColors.color.status.error.solid
                  : semanticColors.color.text.secondary
              }}
            >
              {label}
            </span>
          }
        />
      );
    }

    // Show a chevron whenever the row has descendants — either fetched
    // already or reported by the API's child-org count. Matches the
    // search flow's `childIsKnownFolder` rule in `OrgFilteredTree`:
    // repository-only leaves stay flat (no chevron); only orgs that
    // contain child orgs are expandable.
    const hasChildren =
      (typedModel?.children?.length ?? 0) > 0 ||
      (typedModel?.childCount ?? 0) > 0;
    const Icon = typedModel?.icon;
    const repositoryCount = typedModel?.repositoryCount;
    const isBucket = isNonSelectableId(props.itemId);
    const showRowActions = Boolean(typedModel) && !isBucket;

    const handleContextMenu = showRowActions
      ? (event: React.MouseEvent) => {
          actions?.openRowContextMenu(event, {
            id: typedModel.id,
            name: typedModel.label,
            isEditable: Boolean(typedModel.isEditable)
          });
        }
      : undefined;

    return (
      <TreeItem
        {...props}
        ref={ref}
        slots={{
          ...props.slots,
          iconContainer: hasChildren
            ? ExpandCollapseButton
            : EmptyIconPlaceholder
        }}
        label={
          <Stack
            direction="row"
            onContextMenu={handleContextMenu}
            sx={{
              alignItems: 'center',
              width: '100%',
              gap: 1,
              position: 'relative',
              minWidth: 0
            }}
          >
            {Icon && <Icon size={iconSize.md} style={{ flexShrink: 0 }} />}
            <ModTooltip
              title={
                typeof props.label === 'string' ? props.label : typedModel.label
              }
              enterDelay={500}
            >
              <Stack sx={{ minWidth: 0, flexShrink: 1 }}>
                <MiddleTruncatedLabel
                  label={
                    typeof props.label === 'string'
                      ? props.label
                      : typedModel.label
                  }
                />
              </Stack>
            </ModTooltip>
            {repositoryCount > 0 && (
              <ModTag
                label={<LocaleNumber value={repositoryCount} />}
                size="small"
              />
            )}
            {typedModel && (
              <OrgRowActions model={typedModel} showButton={showRowActions} />
            )}
          </Stack>
        }
      />
    );
  }
);

const EXPAND_COLLAPSE_ATTR = 'data-expand-collapse';

const CollapseIcon: FunctionComponent = () => (
  <ChevronDown size={iconSize.lg} />
);
const ExpandIcon: FunctionComponent = () => <ChevronRight size={iconSize.lg} />;

const ExpandCollapseButton: FunctionComponent<
  React.HTMLAttributes<HTMLDivElement>
> = ({ children, ...rest }) => (
  <ExpandCollapseContainer {...rest} {...{ [EXPAND_COLLAPSE_ATTR]: true }}>
    <ModIconButton size="small" sx={hoverSx}>
      {children}
    </ModIconButton>
  </ExpandCollapseContainer>
);

export type OrganizationsTableRef = {
  /** Attach to the external search input's onKeyDown for ArrowDown/ArrowUp tree focus. */
  handleSearchKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
};

type OrganizationsTableComponentProps = {
  onClose?: () => void;
  // False while the dialog fades out with the table still mounted.
  open?: boolean;
  expandMyOrganizations?: boolean;
};

const OrganizationsTableComponent = React.forwardRef<
  OrganizationsTableRef,
  OrganizationsTableComponentProps
>(({ onClose, open = true, expandMyOrganizations = false }, ref) => {
  const apiRef = useRichTreeViewProApiRef();
  const { data: session } = useSession();
  const currentUserEmail = session?.user?.email;
  const isAdmin = session?.isAdmin ?? false;
  const { selectedOrganizationId, update } = useSelectedOrganizationStore();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [scrolledFromTop, setScrolledFromTop] = useState(false);
  const [scrolledFromBottom, setScrolledFromBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const focusedItemIdRef = useRef<string | null>(null);

  const { watch, setValue, getValues } = useFormContext<{ query: string }>();
  const filterTerm = watch('query') ?? '';
  const isSearching = filterTerm.length >= MIN_SEARCH_TERM_LENGTH;
  const [debouncedTerm] = useDebounce(filterTerm, DOHERTY_THRESHOLD);
  const [pickedMatchIndex, setPickedMatchIndex] = useState<number | null>(null);
  // Apollo's `fetchMore` rejects instead of setting `error`. Keyed by term
  // so a late rejection can't flag the next search; any fresh page 1 (new
  // term, retry, user-org refetch) clears it.
  const [loadMoreFailedTerm, setLoadMoreFailedTerm] = useState<string | null>(
    null
  );

  // ── Search: one page per term ───────────────────────────────────────────
  // Pages merge through the relay policy on `Query.organizations`, so
  // `fetchMore` appends and `refetch` replaces. `cache-first` after the
  // first page keeps cache re-observations off the network (the client
  // default is `network-only`).
  const isSearchSkipped = debouncedTerm.length < MIN_SEARCH_TERM_LENGTH;
  const searchQuery = useOrganizationSelectorGridQuery({
    variables: {
      first: SEARCH_PAGE_SIZE,
      where: { name: { _icontains: debouncedTerm } },
      withExact: true,
      exactWhere: { name: { _in: exactNameCasings(debouncedTerm) } }
    },
    // Gated on the debounced term: `isSearching` flips a debounce early.
    skip: isSearchSkipped,
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true
  });
  const { refetch: refetchSearch, fetchMore: fetchMoreMatches } = searchQuery;
  // Between a keystroke and its debounced fetch the hook still holds the
  // prior term's result; the tree must not flash it.
  const currentResult =
    isSearching && debouncedTerm === filterTerm ? searchQuery.data : undefined;
  const isResultCurrent = currentResult !== undefined;
  const currentTermHasError =
    isSearching &&
    debouncedTerm === filterTerm &&
    (Boolean(searchQuery.error) || loadMoreFailedTerm === debouncedTerm);
  const isLoadingMoreMatches =
    searchQuery.networkStatus === NetworkStatus.fetchMore;

  useEffect(() => {
    if (searchQuery.error) {
      logError(searchQuery.error);
    }
  }, [searchQuery.error]);

  const apolloClient = useApolloClient();

  const copyToClipboard = useCopyToClipboard();

  const copyOrganizationName = useCallback(
    (name: string) => {
      copyToClipboard(name, `Copied "${name}" to clipboard`);
    },
    [copyToClipboard]
  );

  const rowContextMenuPopupState = usePopupState({
    variant: 'popover',
    popupId: 'org-row-context-menu'
  });

  const [rowContextTarget, setRowContextTarget] =
    useState<OrgRowActionTarget | null>(null);

  const openRowContextMenu = useCallback(
    (event: React.MouseEvent, target: OrgRowActionTarget) => {
      event.preventDefault();
      event.stopPropagation();
      setRowContextTarget(target);
      rowContextMenuPopupState.open(event);
    },
    [rowContextMenuPopupState]
  );

  const orgActionsValue = useMemo<OrgActionsContextValue>(
    () => ({
      copyOrganizationName,
      openRowContextMenu
    }),
    [copyOrganizationName, openRowContextMenu]
  );

  const [loadingFolders, setLoadingFolders] = useState<Set<string>>(
    () => new Set()
  );
  const [failedFolders, setFailedFolders] = useState<Set<string>>(
    () => new Set()
  );

  const lazyTreeContextValue = useMemo<LazyTreeContextValue>(
    () => ({ loadingFolders, failedFolders }),
    [loadingFolders, failedFolders]
  );

  // ── Lazy unsearched tree state ──────────────────────────────────────────
  // The unsearched view is built incrementally from per-folder fetches
  // rather than a single all-orgs pagination. The spine to the selected
  // org is bootstrapped via `orgSpineBootstrap`; "My organizations" is
  // looked up by name; Global's first page is fetched directly.
  type LazyAncestor = { id: string; name: string };
  /**
   * Selected org's ancestor chain, **root-most first, selected last**.
   * Empty array when no org is selected or Global is selected.
   */
  const [spine, setSpine] = useState<LazyAncestor[]>([]);
  const [myOrgsRoot, setMyOrgsRoot] = useState<LazyAncestor | null>(null);
  const [topLevelFolderIds, setTopLevelFolderIds] = useState<string[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [treeLoadError, setTreeLoadError] = useState(false);
  // Token that increments to retrigger the mount-time bootstrap effect.
  const [treeLoadAttempt, setTreeLoadAttempt] = useState(0);

  // `isInitialLoading` tracks the *unsearched*-tree bootstrap only. If a
  // search starts before that bootstrap settles, its cleanup cancels the
  // in-flight completion (see the bootstrap effect below) — leaving
  // `isInitialLoading` stuck `true` forever, since nothing else ever clears
  // it while searching. Ignore it while `isSearching`; the search skeleton
  // keys off the search query instead (regression test for #8535: the
  // "hangs and hangs" placeholder rows never clearing).
  const loading = !isSearching && isInitialLoading;

  const retryTreeLoad = useCallback(() => {
    setTreeLoadError(false);
    setTreeLoadAttempt((n) => n + 1);
  }, []);

  const rerunSearch = useCallback(() => {
    setLoadMoreFailedTerm(null);
    void refetchSearch();
  }, [refetchSearch]);

  // ── Lazy-loaded children & sibling counts ───────────────────────────────
  // Shared between the unsearched lazy tree and the search "+ N more"
  // affordance — both fetch a folder's children via the same code path
  // and populate the same map. Reset when the search term changes.
  // `loadingFolders` / `failedFolders` are declared above so the
  // `LazyTreeContext` value can be memoized alongside the rest of the
  // tree state.
  const [revealedChildren, setRevealedChildren] = useState<
    Map<string, RevealedFolderState>
  >(() => new Map());
  const [hiddenSiblingCountByFolder, setHiddenSiblingCountByFolder] = useState<
    Map<string, number>
  >(() => new Map());

  // ── Lazy unsearched tree items ──────────────────────────────────────────
  // Stitched together from `revealedChildren` + spine + myOrgsRoot. Folders
  // whose latest fetched page has `hasMore: true` get a synthetic Show
  // more child appended so the renderer can offer next-page fetching
  // inline. Returns [] until the bootstrap has populated Global's page.
  const treeItems = useMemo<OrganizationTreeItem[]>(() => {
    if (!revealedChildren.has(GLOBAL_ORGANIZATION_ID)) {
      return [];
    }
    const foldersWithMore = new Set<string>();
    const childrenById = new Map<string, OrganizationTreeItem[]>();
    for (const [folderId, state] of revealedChildren.entries()) {
      if (state.hasMore) {
        foldersWithMore.add(folderId);
      }
      childrenById.set(folderId, state.children);
    }
    return buildLazySpineTree({
      revealedChildren: childrenById,
      foldersWithMore,
      spine: spine.length > 0 ? spine : undefined,
      myOrgsRoot: myOrgsRoot ?? undefined
    });
  }, [revealedChildren, spine, myOrgsRoot]);

  // Reveal/loading state from the unsearched tree (or the previous term)
  // must not survive into the new matches.
  useEffect(() => {
    setLoadMoreFailedTerm(null);
    if (debouncedTerm.length < MIN_SEARCH_TERM_LENGTH) {
      return;
    }
    setRevealedChildren(new Map());
    setLoadingFolders(new Set());
    setHiddenSiblingCountByFolder(new Map());
    setFailedFolders(new Set());
    setPickedMatchIndex(null);
  }, [debouncedTerm]);

  const searchPage = currentResult?.organizations;
  const searchEndCursor = searchPage?.pageInfo.hasNextPage
    ? searchPage.pageInfo.endCursor
    : null;
  const handleLoadMoreMatches = useCallback(async () => {
    if (!searchEndCursor || isLoadingMoreMatches) {
      return;
    }
    setLoadMoreFailedTerm(null);
    try {
      await fetchMoreMatches({
        variables: {
          after: searchEndCursor,
          withExact: false,
          exactWhere: undefined
        }
      });
    } catch (e) {
      logError(e);
      // Loaded pages and the "+ N more" row stay, so clicking it retries.
      setLoadMoreFailedTerm(debouncedTerm);
    }
  }, [searchEndCursor, isLoadingMoreMatches, fetchMoreMatches, debouncedTerm]);
  const moreMatchCount =
    searchPage && searchEndCursor
      ? searchPage.count - searchPage.edges.length
      : undefined;

  // ── Spine items: built from server-side search hits ─────────────────────
  const spineItems = useMemo(() => {
    if (!currentResult) {
      return [];
    }
    // An exact hit reappears in its own page; `buildSpineFromHits` keys by id.
    const hits: SpineHitInput[] = [
      ...currentResult.organizations.edges,
      ...(currentResult.exact?.edges ?? [])
    ].map(({ node }) => orgToSpineHit(node, currentUserEmail ?? undefined));
    return buildSpineFromHits({ hits });
  }, [currentResult, currentUserEmail]);

  // ── Search hits in DFS order (drives active-match index) ────────────────
  const searchHits = useMemo(() => {
    if (!isSearching) {
      return [];
    }
    return collectSearchHits({ items: spineItems, searchTerm: debouncedTerm });
  }, [isSearching, spineItems, debouncedTerm]);

  const bestMatchIndex = useMemo(
    () =>
      findBestSearchHitIndex({ hits: searchHits, searchTerm: debouncedTerm }),
    [searchHits, debouncedTerm]
  );
  const activeMatchIndex = pickedMatchIndex ?? bestMatchIndex;

  // Set of every id present in the spine. Used by `fetchChildrenPage` to
  // drop revealed children that already appear in the spine. Memoized so the
  // walk only happens when the spine changes, not on every paged fetch.
  const spineIds = useMemo(() => {
    const ids = new Set<string>();
    const walk = (nodes: OrganizationTreeItem[]): void => {
      for (const node of nodes) {
        ids.add(node.id);
        if (node.children) {
          walk(node.children);
        }
      }
    };
    walk(spineItems);
    return ids;
  }, [spineItems]);

  const fragmentToTreeItem = useCallback(
    (org: OrgChildNode): OrganizationTreeItem => {
      const isUserOrg = isUserOrganization(org);
      const isGlobal = org.id === GLOBAL_ORGANIZATION_ID;
      return {
        id: org.id,
        label: getOrganizationDisplayName(org),
        icon: isGlobal ? GlobalOrganizationIcon : undefined,
        isUserOrganization: isUserOrg,
        isEditable:
          isUserOrg &&
          !!currentUserEmail &&
          (org.user?.email ?? undefined) === currentUserEmail &&
          !isUserOrgBucket(org),
        repositoryCount: org.repositories?.count ?? undefined,
        childCount: org.children?.count ?? undefined
      };
    },
    [currentUserEmail]
  );

  // ── Hidden-sibling counts: derived from `childCount`, already folded
  //     into the search response — no separate network request per spine
  //     node. `handleLoadChildren`/`handleLoadMoreChildren` below still
  //     overwrite individual entries with the exact revealed total once a
  //     folder's real children have been fully paged through.
  useEffect(() => {
    if (!isSearching || spineItems.length === 0) {
      return;
    }
    const initial = new Map<string, number>();
    const walkSpine = (nodes: OrganizationTreeItem[]): void => {
      for (const node of nodes) {
        const shown = node.children?.length ?? 0;
        initial.set(node.id, Math.max(0, (node.childCount ?? 0) - shown));
        if (node.children) {
          walkSpine(node.children);
        }
      }
    };
    walkSpine(spineItems);
    setHiddenSiblingCountByFolder(initial);
  }, [isSearching, spineItems]);

  /**
   * Fetch a single page of `Organization.children`. Returns a discriminated
   * shape so the cursor is required iff the server says there's more — the
   * `{ hasMore: true, cursor: undefined }` middle ground can't be built.
   */
  type FetchChildrenPageResult = {
    children: OrganizationTreeItem[];
  } & ({ hasMore: false } | { hasMore: true; cursor: string });

  const fetchChildrenPage = useCallback(
    async (
      folderId: string,
      after: string | undefined
    ): Promise<FetchChildrenPageResult> => {
      const data = await dataOrThrow(
        apolloClient.query<OrgChildrenQuery, OrgChildrenQueryVariables>({
          query: OrgChildrenDocument,
          variables: {
            id: folderId,
            first: CHILDREN_FETCH_PAGE_SIZE,
            after
          },
          // `no-cache` is critical here. The auto-generated type policies wire
          // `Organization.children` to `relayStylePagination`, which
          // accumulates pages in the cache and returns the merged connection
          // on every read — so subsequent paged fetches would return all
          // previously-fetched edges plus the new page, and our client-side
          // append would duplicate them. Bypassing the cache lets each fetch
          // return only its page.
          fetchPolicy: 'no-cache'
        })
      );
      const edges = data.organization?.children?.edges ?? [];

      const children: OrganizationTreeItem[] = [];
      for (const edge of edges) {
        const node = edge?.node;
        if (!node || spineIds.has(node.id)) {
          continue;
        }
        // Hide a user-org grouping bucket ("Users" / "Users/<email>" / the
        // legacy buckets) that has no children. Deleting the last user org
        // would otherwise leave the synthetic bucket row hanging in Global's
        // children list with nothing inside it.
        if (isUserOrgBucket(node) && (node.children?.count ?? 0) === 0) {
          continue;
        }
        // The viewer's own orgs are pinned under Global as "My organizations"
        // (see fetchMyOrgs), so suppress the raw duplicates from the `Users`
        // branch: hide the whole `Users` grouping for non-admins, and — for
        // admins, who do see everyone else's — hide just their own
        // `Users/<email>` owner bucket within it.
        if (
          (node.id === USERS_GROUPING || node.name === USERS_GROUPING) &&
          !isAdmin
        ) {
          continue;
        }
        if (isOwnUserOrgBucket(node, currentUserEmail)) {
          continue;
        }
        children.push(fragmentToTreeItem(node));
      }

      const pi = data.organization?.children?.pageInfo;
      if (pi?.hasNextPage && pi.endCursor) {
        return { children, hasMore: true, cursor: pi.endCursor };
      }
      return { children, hasMore: false };
    },
    [apolloClient, spineIds, fragmentToTreeItem, isAdmin, currentUserEmail]
  );

  // ── On-demand: fetch the FIRST page of a folder's children. ─────────────
  const handleLoadChildren = useCallback(
    async (folderId: string) => {
      if (revealedChildren.has(folderId) || loadingFolders.has(folderId)) {
        return;
      }
      setLoadingFolders((prev) => new Set(prev).add(folderId));
      // Clear any prior failure marker; if this attempt fails too, the
      // catch block will set it again.
      setFailedFolders((prev) => {
        if (!prev.has(folderId)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(folderId);
        return next;
      });
      try {
        const result = await fetchChildrenPage(folderId, undefined);
        setRevealedChildren((prev) => new Map(prev).set(folderId, result));
        // If there's no more on the server, we know the exact total — drive
        // the "+ N more" suppression. Otherwise leave the count alone so the
        // hint stays visible until the user paginates through everything.
        if (!result.hasMore) {
          setHiddenSiblingCountByFolder((prev) =>
            new Map(prev).set(folderId, result.children.length)
          );
        }
      } catch (e) {
        logError(e);
        setFailedFolders((prev) => new Set(prev).add(folderId));
      } finally {
        setLoadingFolders((prev) => {
          const next = new Set(prev);
          next.delete(folderId);
          return next;
        });
      }
    },
    [revealedChildren, loadingFolders, fetchChildrenPage]
  );

  // ── On-demand: fetch the NEXT page for an already-revealed folder. ──────
  const handleLoadMoreChildren = useCallback(
    async (folderId: string) => {
      const existing = revealedChildren.get(folderId);
      if (!existing || !existing.hasMore) {
        return;
      }
      if (loadingFolders.has(folderId)) {
        return;
      }
      setLoadingFolders((prev) => new Set(prev).add(folderId));
      setFailedFolders((prev) => {
        if (!prev.has(folderId)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(folderId);
        return next;
      });
      try {
        const result = await fetchChildrenPage(folderId, existing.cursor);
        setRevealedChildren((prev) => {
          const current = prev.get(folderId);
          if (!current) {
            return prev;
          }
          const next = new Map(prev);
          const mergedChildren = [...current.children, ...result.children];
          next.set(
            folderId,
            result.hasMore
              ? {
                  children: mergedChildren,
                  hasMore: true,
                  cursor: result.cursor
                }
              : { children: mergedChildren, hasMore: false }
          );
          return next;
        });
        if (!result.hasMore) {
          // We've now fetched everything — count is exact.
          setHiddenSiblingCountByFolder((prev) =>
            new Map(prev).set(
              folderId,
              existing.children.length + result.children.length
            )
          );
        }
      } catch (e) {
        logError(e);
        setFailedFolders((prev) => new Set(prev).add(folderId));
      } finally {
        setLoadingFolders((prev) => {
          const next = new Set(prev);
          next.delete(folderId);
          return next;
        });
      }
    },
    [revealedChildren, loadingFolders, fetchChildrenPage]
  );

  const updateScrollShadows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    setScrolledFromTop(el.scrollTop > 0);
    setScrolledFromBottom(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  // ── Mount / re-bootstrap: parallel lazy fetches ─────────────────────────
  // Replaces the old all-orgs pagination. Three independent fetches run in
  // parallel:
  //   1. `orgChildren(Global, first: 100)` — root page, then each top-level
  //      folder's first page.
  //   2. `myOwnOrganizationsBucket` — the viewer's `Users/<email>` bucket
  //      (so their orgs surface without scrolling the global tree) AND its
  //      first page of children, in one round trip.
  //   3. `orgSpineBootstrap(selectedOrg)` — when an org is selected, get
  //      its `parents` chain. Each ancestor then fans out into its own
  //      `orgChildren` request so siblings are visible at every depth.
  // The effect re-runs when `isSearching` flips back to false (so closing
  // the search restores the unsearched view) and on retry.
  // biome-ignore lint/correctness/useExhaustiveDependencies: bootstrap fires on isSearching/open/selection/retry transitions; fetchChildrenPage/fragmentToTreeItem are stable enough not to drive re-bootstrap
  useEffect(() => {
    if (isSearching || !open) {
      return;
    }
    // Wipe any "+ N more" reveals left over from a previous search before
    // refetching the unsearched tree. Co-located with the bootstrap fetch
    // so it can't race with a debounce-driven reset that fires later.
    setRevealedChildren(new Map());
    setLoadingFolders(new Set());
    setHiddenSiblingCountByFolder(new Map());
    setFailedFolders(new Set());
    let cancelled = false;
    setIsInitialLoading(true);
    setTreeLoadError(false);

    const recordPage = (
      folderId: string,
      result: FetchChildrenPageResult
    ): void => {
      if (cancelled) {
        return;
      }
      setRevealedChildren((prev) => new Map(prev).set(folderId, result));
    };

    // Spine and top-level folders overlap (e.g. a selected org under ALL).
    const firstPageFetches = new Map<string, Promise<boolean>>();
    const fetchFirstPage = (folderId: string): Promise<boolean> => {
      let pending = firstPageFetches.get(folderId);
      if (!pending) {
        pending = fetchChildrenPage(folderId, undefined)
          .then((r) => {
            recordPage(folderId, r);
            return true;
          })
          .catch((e) => {
            logError(e);
            return false;
          });
        firstPageFetches.set(folderId, pending);
      }
      return pending;
    };

    const fetchGlobal = fetchChildrenPage(GLOBAL_ORGANIZATION_ID, undefined)
      .then(async (result) => {
        recordPage(GLOBAL_ORGANIZATION_ID, result);
        const folderIds = result.children
          .filter(
            (child) =>
              (child.childCount ?? 0) > 0 && !isNonSelectableId(child.id)
          )
          .map((child) => child.id);
        const loaded = await Promise.all(folderIds.map(fetchFirstPage));
        // A failed folder starts collapsed; expanding it retries.
        if (!cancelled) {
          setTopLevelFolderIds(folderIds.filter((_, i) => loaded.at(i)));
        }
      })
      .catch((e) => {
        logError(e);
        if (!cancelled) {
          setTreeLoadError(true);
        }
      });

    // The viewer's own orgs hang off their `Users/<email>` owner bucket, which
    // is named after the email — so it's looked up by name.
    type BucketNode = NonNullable<
      NonNullable<
        NonNullable<
          MyOwnOrganizationsBucketQuery['organizations']['edges']
        >[number]
      >['node']
    >;
    const fetchMyOrgs = (async (): Promise<void> => {
      if (!currentUserEmail) {
        return;
      }
      const emailData = await dataOrThrow(
        apolloClient.query<
          MyOwnOrganizationsBucketQuery,
          MyOwnOrganizationsBucketQueryVariables
        >({
          query: MyOwnOrganizationsBucketDocument,
          variables: { email: currentUserEmail },
          fetchPolicy: 'cache-first'
        })
      );
      const emailNode = emailData.organizations?.edges?.[0]?.node ?? undefined;
      // `name _eq email` can match any node named like the email; only accept
      // the actual `Users/<email>` owner bucket, never a same-named collision.
      const bucketNode: BucketNode | undefined = isOwnUserOrgBucket(
        emailNode,
        currentUserEmail
      )
        ? emailNode
        : undefined;
      if (cancelled || !bucketNode) {
        return;
      }
      // The owner bucket is named after the email; relabel it to the familiar
      // "My organizations".
      setMyOrgsRoot({ id: bucketNode.id, name: MY_ORGANIZATIONS_GROUPING });
      const pageInfo = bucketNode.children?.pageInfo;
      const children: OrganizationTreeItem[] = [];
      for (const edge of bucketNode.children?.edges ?? []) {
        const node = edge?.node;
        if (node) {
          children.push(fragmentToTreeItem(node));
        }
      }
      const page: FetchChildrenPageResult =
        pageInfo?.hasNextPage && pageInfo.endCursor
          ? { children, hasMore: true, cursor: pageInfo.endCursor }
          : { children, hasMore: false };
      recordPage(bucketNode.id, page);
    })().catch((e) => {
      // My organizations is a non-critical surface — log and continue.
      logError(e);
    });

    const fetchSpine =
      selectedOrganizationId &&
      selectedOrganizationId !== GLOBAL_ORGANIZATION_ID
        ? dataOrThrow(
            apolloClient.query<
              OrgSpineBootstrapQuery,
              OrgSpineBootstrapQueryVariables
            >({
              query: OrgSpineBootstrapDocument,
              variables: { id: selectedOrganizationId },
              fetchPolicy: 'cache-first'
            })
          )
            .then((data) => {
              if (cancelled) {
                return;
              }
              const org = data.organization;
              if (!org) {
                return;
              }
              // `parents` come immediate-parent-first, ordered toward root.
              // The spine state stores them root-most first with the
              // selected org appended last so `buildLazySpineTree` can
              // walk from Global downward.
              const parents = [...(org.parents ?? [])].reverse();
              const spineChain: LazyAncestor[] = [
                ...parents,
                { id: org.id, name: org.name }
              ];
              setSpine(spineChain);

              // Fan out children fetches for each spine ancestor (we
              // already fetched Global above) and for the selected org
              // itself if it can have children.
              const childCount = org.children?.count ?? 0;
              const ancestorFetches: Promise<boolean>[] = [];
              for (const ancestor of spineChain) {
                if (ancestor.id === GLOBAL_ORGANIZATION_ID) {
                  continue;
                }
                if (ancestor.id === org.id && childCount === 0) {
                  continue;
                }
                ancestorFetches.push(fetchFirstPage(ancestor.id));
              }
              return Promise.all(ancestorFetches);
            })
            .catch((e) => {
              // A stale `selectedOrganizationId` (org deleted, missing
              // permissions, etc.) lands here — the tree still renders
              // with whatever else we managed to fetch.
              logError(e);
            })
        : Promise.resolve();

    Promise.all([fetchGlobal, fetchMyOrgs, fetchSpine]).then(() => {
      if (!cancelled) {
        setIsInitialLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
    // Re-bootstrap once the session resolves so the email-keyed "My
    // organizations" bucket lookup and the admin/non-admin grouping filter run
    // with the real viewer identity.
  }, [
    isSearching,
    open,
    selectedOrganizationId,
    treeLoadAttempt,
    currentUserEmail,
    isAdmin
  ]);

  // Drop every piece of tree state and let the bootstrap rebuild from
  // scratch. Used for both create and rename/delete: each path has
  // multiple sources of staleness (revealedChildren map, spine ancestor
  // chain pinning a now-stale org to the top of its folder,
  // expandedItems keeping the wrong folder open, and on create the new
  // org needs to be both visible *and* highlighted as selected) and
  // wiping everything is the only reliable way to guarantee no stale
  // ref survives. The bootstrap re-runs with the latest
  // selectedOrganizationId, repopulates spine/Global/Bucket, and the
  // auto-expand effect re-applies sensible defaults including the new
  // org's spine.
  const resetTreeState = useCallback(() => {
    setRevealedChildren(new Map());
    setHiddenSiblingCountByFolder(new Map());
    setExpandedItems([]);
    setSpine([]);
    setTreeLoadAttempt((n) => n + 1);
    // An active search re-runs too, so a renamed org never shows stale.
    // `refetch` ignores `skip`, hence the guard.
    if (!isSearchSkipped) {
      rerunSearch();
    }
  }, [isSearchSkipped, rerunSearch]);

  userOrgCreatedEvent.useListener(resetTreeState);
  userOrgChangedEvent.useListener(resetTreeState);

  // Expand top-level folders, plus the spine so the selected org is visible.
  useEffect(() => {
    if (isSearching) {
      requestAnimationFrame(updateScrollShadows);
      return;
    }
    setExpandedItems(
      getDefaultExpandedItems({
        topLevelFolderIds,
        spine,
        myOrgsRoot,
        expandMyOrganizations
      })
    );
    requestAnimationFrame(updateScrollShadows);
  }, [
    isSearching,
    topLevelFolderIds,
    spine,
    expandMyOrganizations,
    myOrgsRoot,
    updateScrollShadows
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: treeItems is an intentional trigger — reset tracked focus when the visible tree changes
  useEffect(() => {
    focusedItemIdRef.current = null;
  }, [treeItems]);

  const handleItemFocus = useCallback(
    (_event: React.SyntheticEvent | null, itemId: string) => {
      focusedItemIdRef.current = itemId;
    },
    []
  );

  const handleSelectOrg = useCallback(
    (itemId: string) => {
      if (!isSelectableOrgId(itemId)) {
        return;
      }
      if (itemId !== selectedOrganizationId) {
        update({ selectedOrganizationId: itemId });
      }
      onClose?.();
    },
    [selectedOrganizationId, update, onClose]
  );

  // Triggered by `RichTreeViewPro`'s onExpandedItemsChange. Detects newly
  // expanded folders that haven't been fetched yet and kicks off their
  // first-page request — drives the on-demand expansion of non-spine
  // branches. Non-selectable bucket ids ("My organizations" / "User
  // organizations") still need their children fetched on expand —
  // selection is blocked separately in handleSelectOrg / handleTreeKeyDown.
  // "My organizations" is also pre-fetched at bootstrap and short-circuits
  // via the revealedChildren guard.
  const handleExpandedItemsChange = useCallback(
    (_event: React.SyntheticEvent | null, newItems: string[]) => {
      setExpandedItems((prev) => {
        const prevSet = new Set(prev);
        for (const id of newItems) {
          if (
            !prevSet.has(id) &&
            !revealedChildren.has(id) &&
            !loadingFolders.has(id) &&
            !isShowMoreItemId(id) &&
            !isPlaceholderItemId(id)
          ) {
            handleLoadChildren(id);
          }
        }
        return newItems;
      });
    },
    [revealedChildren, loadingFolders, handleLoadChildren]
  );

  const handleSearchKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (isSearching) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          // No pick before hits arrive, or it would override the best match.
          if (searchHits.length > 0) {
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setPickedMatchIndex(
              Math.min(
                Math.max(activeMatchIndex + step, 0),
                searchHits.length - 1
              )
            );
          }
        } else if (event.key === 'Enter' && searchHits.length > 0) {
          event.preventDefault();
          const hit = searchHits[activeMatchIndex];
          if (hit) {
            handleSelectOrg(hit.item.id);
          }
        }
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const firstItemId = treeItems.at(0)?.id;
        if (firstItemId && apiRef.current) {
          apiRef.current.focusItem(event, firstItemId);
        }
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        const lastItemId = getLastVisibleItemId({
          items: treeItems,
          expandedItems
        });
        if (lastItemId && apiRef.current) {
          apiRef.current.focusItem(event, lastItemId);
        }
      }
    },
    [
      apiRef,
      treeItems,
      expandedItems,
      isSearching,
      searchHits,
      activeMatchIndex,
      handleSelectOrg
    ]
  );

  const handleTreeKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        const focusedId = focusedItemIdRef.current;
        if (!focusedId || !isSelectableOrgId(focusedId)) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        if (focusedId !== selectedOrganizationId) {
          update({ selectedOrganizationId: focusedId });
        }
        onClose?.();
      } else if (
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        event.preventDefault();
        setValue('query', (getValues('query') ?? '') + event.key);
        document.getElementById(ORG_SEARCH_INPUT_ID)?.focus();
      }
    },
    [selectedOrganizationId, update, onClose, setValue, getValues]
  );

  useImperativeHandle(
    ref,
    () => ({
      handleSearchKeyDown
    }),
    [handleSearchKeyDown]
  );

  const handleItemClick = useCallback(
    (event: React.SyntheticEvent, itemId: string) => {
      const target = event.target as HTMLElement;
      // Chevron clicks bubble to content's onClick — let them only toggle
      // expansion (handled by the tree view) and never select.
      if (
        target.closest(`[${EXPAND_COLLAPSE_ATTR}]`) ||
        target.closest(`[${ACTIONS_ATTR}]`)
      ) {
        return;
      }
      // Show more rows: fetch the next page for the parent folder; don't
      // select anything, don't close the dialog.
      if (isShowMoreItemId(itemId)) {
        const folderId = showMoreItemFolderId(itemId);
        handleLoadMoreChildren(folderId);
        return;
      }
      // Grouping headers can't be selected — toggle their expansion so a
      // row-body click still has an effect.
      if (isNonSelectableId(itemId)) {
        setExpandedItems((prev) =>
          prev.includes(itemId)
            ? prev.filter((id) => id !== itemId)
            : [...prev, itemId]
        );
        return;
      }
      handleSelectOrg(itemId);
    },
    [handleSelectOrg, handleLoadMoreChildren]
  );

  return (
    <OrgActionsContext.Provider value={orgActionsValue}>
      <LazyTreeContext.Provider value={lazyTreeContextValue}>
        <Stack sx={{ flex: 1, minHeight: 0 }}>
          {/* Tree content */}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <Stack
            ref={scrollRef}
            onScroll={updateScrollShadows}
            onKeyDown={handleTreeKeyDown}
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              transition: 'box-shadow 0.2s ease',
              ...(scrolledFromTop && {
                boxShadow: toBoxShadow(shadows.neutral.small)
              })
            }}
          >
            {((loading && treeItems.length === 0) ||
              (isSearching && !currentTermHasError && !isResultCurrent)) && (
              <Stack data-testid="org-selector-skeleton">
                {SKELETON_WIDTHS.map((width, i) => (
                  <Stack
                    key={i}
                    direction="row"
                    sx={{
                      gap: 1,
                      alignItems: 'center',
                      minHeight: TREE_ROW_MIN_HEIGHT,
                      py: 0.5,
                      pr: 1,
                      pl: `${TREE_ITEM_INDENT + TREE_ITEM_BASE_PL}px`,
                      borderBottom: `1px solid ${semanticColors.color.border.primary}`
                    }}
                  >
                    <ModSkeleton width={`${width}%`} height={14} />
                  </Stack>
                ))}
              </Stack>
            )}
            {!loading &&
              treeLoadError &&
              treeItems.length === 0 &&
              !isSearching && (
                <InlineTreeMessage
                  testId="org-selector-load-error"
                  message="Couldn't load organizations."
                  onRetry={retryTreeLoad}
                />
              )}
            {isSearching && currentTermHasError && searchHits.length === 0 && (
              <InlineTreeMessage
                testId="org-selector-search-error"
                message="Couldn't search organizations."
                onRetry={rerunSearch}
              />
            )}
            {isSearching &&
              !currentTermHasError &&
              isResultCurrent &&
              searchHits.length === 0 && (
                <Stack data-testid="org-selector-no-results">
                  <EmptyState
                    icon={<OrganizationIcon size={iconSize.xxl} />}
                    title="No organizations found"
                  />
                </Stack>
              )}
            {isSearching && currentTermHasError && searchHits.length > 0 && (
              <ModBanner
                data-testid="org-selector-partial-results-warning"
                intent="warning"
                type="outlined"
                message="Couldn't load all results — showing what's loaded so far."
                messagePosition="left"
              />
            )}
            {filterTerm.length > 0 && !isSearching && (
              <InlineTreeMessage
                testId="org-selector-search-too-short"
                message={`Type at least ${MIN_SEARCH_TERM_LENGTH} characters to search`}
              />
            )}
            {isSearching ? (
              <OrgFilteredTree
                // Remount per term: resets expansion, re-scrolls to the best match.
                key={debouncedTerm}
                spineItems={spineItems}
                searchTerm={debouncedTerm}
                searchHits={searchHits}
                activeMatchIndex={activeMatchIndex}
                moreMatchCount={moreMatchCount}
                isLoadingMoreMatches={isLoadingMoreMatches}
                onLoadMoreMatches={handleLoadMoreMatches}
                hiddenSiblingCountByFolder={hiddenSiblingCountByFolder}
                revealedChildren={revealedChildren}
                loadingFolders={loadingFolders}
                failedFolders={failedFolders}
                onLoadChildren={handleLoadChildren}
                onLoadMoreChildren={handleLoadMoreChildren}
                onSelect={handleSelectOrg}
                onMatchClick={setPickedMatchIndex}
                onOpenRowContextMenu={openRowContextMenu}
              />
            ) : (
              <RichTreeViewPro
                apiRef={apiRef}
                items={treeItems}
                itemChildrenIndentation={TREE_ITEM_INDENT}
                itemHeight={TREE_ROW_MIN_HEIGHT}
                expandedItems={expandedItems}
                onExpandedItemsChange={handleExpandedItemsChange}
                expansionTrigger="iconContainer"
                selectedItems={selectedOrganizationId ?? ''}
                onItemClick={handleItemClick}
                onItemFocus={handleItemFocus}
                slots={{
                  item: CustomTreeItem,
                  collapseIcon: CollapseIcon,
                  expandIcon: ExpandIcon
                }}
                sx={{
                  [`& .${treeItemClasses.content}`]: {
                    // MUI X TreeView v9 injects 4px vertical padding by default
                    // and pins `.content` to `height: var(--TreeView-itemHeight)`.
                    // Zero the padding + free the height so rows grow with the
                    // label's own margin when wrapping to a second line.
                    py: 0,
                    pr: 1,
                    pl: `calc(var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth) + ${TREE_ITEM_BASE_PL}px)`,
                    minHeight: TREE_ROW_MIN_HEIGHT,
                    height: 'auto',
                    borderRadius: 0,
                    cursor: 'pointer',
                    borderBottom: `1px solid ${semanticColors.color.border.primary}`,
                    '&:hover': {
                      backgroundColor: semanticColors.button.secondary.bgHover
                    }
                  },
                  // MUI X v9 flags focus with `data-focused`, not `.Mui-focused`.
                  [`& .${treeItemClasses.content}[data-focused]`]: {
                    backgroundColor: semanticColors.button.secondary.bgHover
                  },
                  [`& :focus-visible > .${treeItemClasses.content}[data-focused]`]:
                    TREE_ROW_FOCUS_RING_SX,
                  [`& .${treeItemClasses.content}.Mui-selected`]: {
                    backgroundColor: semanticColors.button.secondary.bgHover
                  },
                  [`& .${treeItemClasses.content}.Mui-selected:hover`]: {
                    backgroundColor: semanticColors.button.secondary.bgPressed
                  },
                  [`& .${treeItemClasses.label}`]: {
                    fontSize: typography.fontSize.sm,
                    fontWeight: typography.fontWeight.regular,
                    lineHeight: '20px'
                  },
                  [`& .${treeItemClasses.groupTransition}`]: {
                    ml: 0
                  },
                  [`& .${treeItemClasses.iconContainer}`]: {
                    color: semanticColors.color.icon.utility,
                    mr: 0.5
                  },
                  ...(loading && { opacity: 0.6 })
                }}
              />
            )}
          </Stack>

          {/* Footer */}
          <Stack
            sx={{
              flexShrink: 0,
              transition: 'box-shadow 0.2s ease',
              ...(scrolledFromBottom && {
                boxShadow: toBoxShadow({
                  ...shadows.neutral.small,
                  y: -shadows.neutral.small.y
                })
              })
            }}
          >
            <OrganizationsFooter onClose={onClose} />
          </Stack>
        </Stack>

        <RowContextMenu
          popupState={rowContextMenuPopupState}
          target={rowContextTarget}
          actions={orgActionsValue}
        />
      </LazyTreeContext.Provider>
    </OrgActionsContext.Provider>
  );
});

type RowContextMenuProps = {
  popupState: ReturnType<typeof usePopupState>;
  target: OrgRowActionTarget | null;
  actions: OrgActionsContextValue;
};

const RowContextMenu: FunctionComponent<RowContextMenuProps> = ({
  popupState,
  target,
  actions
}) => {
  if (!target) {
    return null;
  }

  const handleCopy = () => {
    popupState.close();
    actions.copyOrganizationName(target.name);
  };

  const compactItemSx = {
    minHeight: 'auto',
    py: 0.5,
    px: 1.25,
    fontSize: typography.fontSize.sm,
    [`& .${listItemIconClasses.root}`]: {
      minWidth: 'auto',
      mr: 1
    }
  };

  return (
    <ModMenu
      {...bindMenu(popupState)}
      slotProps={{
        list: { dense: true, sx: { py: 0.5 } }
      }}
    >
      <ModMenuItem
        icon={<CopyIcon size={iconSize.sm} />}
        onClick={handleCopy}
        sx={compactItemSx}
      >
        Copy name
      </ModMenuItem>
    </ModMenu>
  );
};

export const OrganizationsTable = React.forwardRef<
  OrganizationsTableRef,
  OrganizationsTableComponentProps
>((props, ref) => (
  <OrganizationsTableErrorBoundary>
    <OrganizationsTableComponent ref={ref} {...props} />
  </OrganizationsTableErrorBoundary>
));
